import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { AppChrome } from "./AppChrome";
import {
  Container,
  Typography,
  TextField,
  CircularProgress,
  LinearProgress,
  Grid,
  makeStyles,
  Box,
  Button,
} from "@material-ui/core";
import { useUser } from "auth/UserContext";
import firebase from "firebase/app";
import { useDebouncedLinker } from "./data/useDebouncedLinker";
import { Subscription, UserProfile } from "@logotron/shared";
import { SubscriptionExpirationDate } from "controls/SubscriptionExpirationDate";

type DocumentReference = firebase.firestore.DocumentReference;

const useStyles = makeStyles((theme) => ({
  large: {
    width: theme.spacing(7),
    height: theme.spacing(7),
  },
  field: { margin: `${theme.spacing(2)}px 0` },
  enterButton: { margin: `${theme.spacing(2)}px 0` },
}));

function UserProfileEdit(props: {
  user: firebase.User;
  userDocRef: DocumentReference;
  profile: UserProfile;
}) {
  const classes = useStyles();
  const paymentPending = window.location.search.includes("pending");
  const { user, userDocRef, profile } = props;
  const [link] = useDebouncedLinker(
    profile,
    useCallback(
      (changeset) => {
        console.log("change!", changeset);
        userDocRef.update(changeset).catch((err) => console.log(err));
      },
      [userDocRef]
    )
  );

  const [subscription, setSubscription] = useState<
    Subscription | "loading" | undefined
  >("loading");

  useEffect(() => {
    const docRef = firebase
      .firestore()
      .collection("users")
      .doc(user.uid)
      .collection("subscriptions")
      .doc("current");
    const unsubscribe = docRef.onSnapshot((snapshot) => {
      const sub = snapshot.data() as Subscription;
      setSubscription(sub);
    });
    return () => unsubscribe();
  }, [user.uid]);

  return (
    <Grid container spacing={4}>
      <Grid item container xs={12} sm={6}>
        <Grid item xs={12}>
          <Box className={classes.field}>
            <Typography variant="h5">Předplatné</Typography>
          </Box>
          <Box className={classes.field}>
            {subscription === "loading" ? (
              <CircularProgress />
            ) : (subscription?.expiryDate ?? 0) > new Date().valueOf() ? (
              <>
                <Typography variant="h6">Aktivní</Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  component={RouterLink}
                  to="/app"
                  className={classes.enterButton}
                >
                  Vstoupit do aplikace
                </Button>
                {subscription?.lastKnownStatus === "CANCELLED" && (
                  <p>
                    Vaše předplatné bylo zrušeno. Přístup k aplikaci vyprší ke
                    dni{" "}
                    <SubscriptionExpirationDate
                      timestamp={subscription.expiryDate}
                    />
                    . Pokud budete chtít Logotron dál používat, budete moci po
                    tomto datu předplatné obnovit.
                  </p>
                )}
                {subscription?.type === "paypal" &&
                  subscription.lastKnownStatus !== "CANCELLED" && (
                    <p>
                      Vaše předplatné můžete zrušit{" "}
                      <RouterLink to="/app/zruseni">zde</RouterLink>.
                    </p>
                  )}
              </>
            ) : (
              <>
                <Typography variant="h6">
                  Vaše předplatné není aktivní
                </Typography>
                {subscription?.expiration ? (
                  <>
                    <p>
                      Vaše předplatné skončilo k datu: {subscription.expiration}
                    </p>
                    <p>
                      Pokud jste předplatné nezrušili, zkontolujte si prosím
                      svůj PayPal účet, zda platba na další období proběhla.
                    </p>
                  </>
                ) : (
                  <p></p>
                )}
                <Button
                  variant="contained"
                  color="secondary"
                  component={RouterLink}
                  to="/app/predplatne"
                >
                  Vybrat plán předplatného
                </Button>
                {paymentPending && (
                  <>
                    <Typography variant="h6">Zpracovává se...</Typography>
                    <LinearProgress />
                    <p>
                      Čekáme na potvrzení od PayPalu. Většinou to trvá jen pár
                      minut!
                    </p>
                  </>
                )}
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box className={classes.field}>
          <Typography variant="h5">Základní údaje</Typography>
        </Box>
        <Box className={classes.field}>
          <TextField label="Email" disabled fullWidth value={profile.email} />
        </Box>
        <Box className={classes.field}>
          <TextField
            label="Zobrazované jméno"
            fullWidth
            required
            {...link("displayName")}
            inputProps={{ maxLength: 50 }}
          />
        </Box>
        <Box className={classes.field}>
          <TextField
            label="Telefon"
            fullWidth
            {...link("phoneNumber")}
            inputProps={{ maxLength: 15 }}
          />
        </Box>
        <Box className={classes.field}>
          {user.providerData
            .filter<firebase.UserInfo>((x): x is firebase.UserInfo => !!x)
            .map((userInfo, idx) => (
              <TextField
                key={idx}
                label="Přihlašovací metoda"
                value={userInfo.providerId}
                disabled
              />
            ))}
        </Box>
      </Grid>
    </Grid>
  );
}

export function UserProfilePage() {
  const { user, userDocRef, profile } = useUser();
  const loading = !userDocRef || !profile || !user; // this is in protected path

  return (
    <AppChrome routingPath={[]}>
      <Container maxWidth="md">
        <Typography variant="h2">Můj profil</Typography>
        {loading ? (
          <div>
            <CircularProgress />
          </div>
        ) : (
          <UserProfileEdit
            user={user!}
            userDocRef={userDocRef!}
            profile={profile!}
          />
        )}
        <Box>
          <footer>
            <p>Copyright (c) Martina Kolmanová, Veronika Kašovská</p>
            <p>
              <RouterLink to="/ochrana-osobnich-udaju">
                Ochrana osobních údajů
              </RouterLink>
            </p>
          </footer>
        </Box>
      </Container>
    </AppChrome>
  );
}

/*
 
      <Grid item xs={12} sm={6}>
        <Box className={classes.field}>
          <Typography variant="h5">Adresa</Typography>
        </Box>
        <Box className={classes.field}>
          <TextField
            label="Jméno a příjmení"
            fullWidth
            required
            {...link("billingName")}
          />
        </Box>
        <Box className={classes.field}>
          <TextField
            label="Ulice"
            fullWidth
            required
            {...link("billingStreet")}
          />
        </Box>
        <Box className={classes.field}>
          <TextField
            label="Město"
            fullWidth
            required
            {...link("billingCity")}
          />
        </Box>
        <Box className={classes.field}>
          <TextField label="PSČ" fullWidth required {...link("billingPostalCode")} />
        </Box>
      </Grid>
*/
